import React, { useEffect, useMemo, useState } from 'react';
import { useMaybeAuth } from '../auth/AuthContext';
import { useMinimumVisible } from '../util/loading';
import { isAuthDevModalEnabled, isAuthUiEnabled } from '../util/featureFlags';
import { presentsAsSignedOut } from '../util/tierGating';
import { REQUEST_ACCOUNT_EVENT, REQUEST_SIGN_IN_EVENT, type AccountNotice } from '../util/authEvents';
import { clearBillingIntent } from '../services/billingIntent';
import AccountModal from './AccountModal';
import AuthModal from './AuthModal';
import Skeleton from './Skeleton';
import './AuthLauncher.scss';

export default function AuthLauncher() {
  const auth = useMaybeAuth();
  const rolesEnabled = auth?.rolesEnabled ?? false;
  // Recompute when the server master switch arrives (async), not just at mount.
  const enabled = useMemo(
    () => rolesEnabled || isAuthUiEnabled() || isAuthDevModalEnabled(),
    [rolesEnabled],
  );
  const user = auth?.user || null;
  // An admin acting as Guest is signed out as far as the server is concerned — it withholds
  // the account and a chop it makes is unowned — so the corner button must say so too.
  const signedOut = presentsAsSignedOut(user);
  /*
   * Until /api/me answers we do not know WHICH button this is, and `user: null` reads as signed
   * out — so a signed-in visitor got "Sign up / Sign in" for the length of that request, then
   * watched it shrink to "My Account" and jump 56px right (the button is right-anchored and the
   * two labels are 152px and 96px wide). Measured at 154ms → 733ms on a throttled load. Neither
   * label is safe to guess, and the wrong one invites a click that opens the wrong dialog.
   *
   * So the corner shows a skeleton until the label is known, held for the app's usual minimum so
   * it cannot itself flash past. Rendering NOTHING would also avoid the wrong label and would
   * avoid the resize below — but it makes the corner look empty rather than pending, and it is
   * the one loading state on screen that every page shares, so it follows the same rule as the
   * pricing CTAs instead of inventing a second one.
   *
   * The skeleton is a fixed width and the real labels are not, so the button does change size
   * once. That is the accepted cost: a corner element is `position: fixed` and shifts nothing
   * around it, whereas a wrong label is wrong.
   *
   * Gates the BUTTON only. The dialogs below still mount while this is true, because a deep link
   * (?reset-token=…) has to be able to open one before /api/me has said anything.
   */
  const labelPending = auth?.isLoading ?? false;
  const labelPendingHeld = useMinimumVisible(labelPending);
  const labelUnknown = labelPending || labelPendingHeld;

  // Detect deep-link params from URL
  const deepLink = useMemo(() => {
    try {
      const qs = new URLSearchParams(window.location.search);

      // Password reset: /?reset-token=TOKEN&email=EMAIL
      const resetToken = qs.get('reset-token');
      if (resetToken) {
        return { type: 'reset' as const, token: resetToken, email: qs.get('email') || '' };
      }

      // Auth redirect: /?auth-redirect=URL (e.g. email verification link when not logged in)
      const authRedirect = qs.get('auth-redirect');
      if (authRedirect) {
        return { type: 'auth-redirect' as const, url: authRedirect };
      }

      return null;
    } catch (_e) {
      return null;
    }
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [shouldMount, setShouldMount] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [shouldMountAccount, setShouldMountAccount] = useState(false);
  const [accountNotice, setAccountNotice] = useState<AccountNotice | null>(null);

  // Auto-open modal for deep links
  useEffect(() => {
    if (deepLink) {
      setShouldMount(true);
      setIsOpen(true);
      // Clean the URL so tokens don't linger
      try {
        window.history.replaceState({}, '', '/');
      } catch (_e) {
        // ignore
      }
    }
  }, [deepLink]);

  // Something on the page asked for the sign-in dialog (util/authEvents). Ignored while already
  // signed in — the asker, the chop error banner, only offers it once the session has ended.
  useEffect(() => {
    const open = (): void => {
      if (!signedOut) return;
      setShouldMount(true);
      setIsOpen(true);
    };
    window.addEventListener(REQUEST_SIGN_IN_EVENT, open);
    return () => window.removeEventListener(REQUEST_SIGN_IN_EVENT, open);
  }, [user]);

  // Something asked for My Account — today only a sign-in from "Get Pro" by someone who already has
  // Pro. Not gated on `user`: it fires from inside login(), before this component has re-rendered
  // with the new user, and AccountPanel reads the user from context anyway.
  useEffect(() => {
    const open = (e: Event): void => {
      setAccountNotice((e as CustomEvent<{ notice?: AccountNotice }>).detail?.notice ?? null);
      setIsOpen(false);
      setShouldMountAccount(true);
      setIsAccountOpen(true);
    };
    window.addEventListener(REQUEST_ACCOUNT_EVENT, open);
    return () => window.removeEventListener(REQUEST_ACCOUNT_EVENT, open);
  }, []);

  // After login, if there's an auth-redirect URL, navigate to it
  // (e.g. email verification link that required login first)
  useEffect(() => {
    if (user && deepLink?.type === 'auth-redirect') {
      window.location.assign(deepLink.url);
    }
  }, [user, deepLink]);

  if (!enabled) return null;

  return (
    <>
      {/* Signed in: one button, opening the account dialog over whatever you were doing. It used to
          navigate to /settings, which threw away unchopped editor work to read an email address.
          The page still exists for Stripe's portal return and deep links (views/Settings.tsx). */}
      {labelUnknown ? (
        <Skeleton className="auth-launcher-skeleton" />
      ) : (!signedOut ? (
        <button
          type="button"
          className="auth-launcher-btn"
          onClick={ () => {
            setAccountNotice(null);
            setShouldMountAccount(true);
            setIsAccountOpen(true);
          } }
          aria-haspopup="dialog"
          aria-expanded={ isAccountOpen ? 'true' : 'false' }
        >
          My Account
        </button>
      ) : (
        <button
          type="button"
          className="auth-launcher-btn"
          onClick={ () => {
            setShouldMount(true);
            setIsOpen(true);
          } }
          aria-haspopup="dialog"
          aria-expanded={ isOpen ? 'true' : 'false' }
        >
          Sign up / Sign in
        </button>
      ))}
      {shouldMount ? (
        <AuthModal
          isOpen={ isOpen }
          onClose={ () => {
            // A "Get Pro" that led here ends with the dialog. A successful sign-in has already
            // used it by now (login() runs before the dialog closes itself); anything else means
            // they walked away, and the click must not fire on some later, unrelated sign-in.
            clearBillingIntent();
            setIsOpen(false);
          } }
          onExited={ () => setShouldMount(false) }
          initialMode={ deepLink?.type === 'reset' ? 'reset' : undefined }
          resetToken={ deepLink?.type === 'reset' ? deepLink.token : undefined }
          resetEmail={ deepLink?.type === 'reset' ? deepLink.email : undefined }
          onOpenAccount={ () => {
            // Close the auth dialog and open the account one in its place; the editor underneath
            // is untouched either way.
            setIsOpen(false);
            setShouldMountAccount(true);
            setIsAccountOpen(true);
          } }
        />
      ) : null}
      {shouldMountAccount ? (
        <AccountModal
          isOpen={ isAccountOpen }
          onClose={ () => setIsAccountOpen(false) }
          onExited={ () => {
            setShouldMountAccount(false);
            setAccountNotice(null);
          } }
          notice={ accountNotice }
        />
      ) : null}
    </>
  );
}
